import React from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  CalendarDays,
  Download,
  RefreshCcw,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { supabase } from '../supabaseClient';
import {
  analyticsDateRange,
  buildDailyTrend,
  isJoinOrEntryEvent,
  localDateKey,
  summarizeAnalytics,
  todayInputValue,
} from './appAnalyticsSummary';

const EVENT_LIMIT = 5000;
const SIGNUP_PAGE_SIZE = 10;
const CHART_COLORS = ['#1377a8', '#16a79e', '#f4a340', '#8b76d7', '#dd718a', '#64748b', '#16a34a'];
const PLATFORM_COLORS = { IOS: '#1377a8', ANDROID: '#16a79e', WEB: '#8b76d7', UNKNOWN: '#94a3b8' };
// Navigation-only events do not express a meaningful user choice. The dashboard
// reports actions only, and filters historic rows that predate this change.
const EXCLUDED_ANALYTICS_EVENTS = new Set([
  'screen_viewed',
  'leaderboard_opened',
  'activity_tracker_opened',
  'activity_history_opened',
  'highlights_opened',
  'fitness_section_opened',
  'map_opened',
  'chat_hub_opened',
  'community_hub_opened',
  'workout_groups_opened',
  'store_opened',
  'bookings_opened',
]);
const EVENT_LABELS = {
  activity_tracking_started: 'Started activity tracking',
  activity_tracking_completed: 'Completed activity tracking',
  activity_saved: 'Saved an activity',
  complete_registration: 'Registration tracking event',
  proam_profile_viewed: 'ProAm profile viewed',
  step_tracking_activated: 'Activated step tracking',
  step_challenge_joined: 'Joined a step challenge',
  leaderboard_share_card_shared: 'Shared leaderboard card',
  activity_share_card_shared: 'Shared activity',
};
const SCREEN_LABELS = {
  HomeScreen: 'Home',
  ActivityTracker: 'Track activity',
  ActivityHistory: 'Activity history',
  ChatScreen: 'Messages',
  CommunityHub: 'Communities',
  MapScreen: 'Sports map',
  ProAmProfile: 'Profile',
  ProAmStore: 'Store',
  WorkoutGroups: 'Workout groups',
  ViewScreen: 'Details',
  fitness: 'Fitness',
  activity_tracker: 'Activity tracker',
  proam_profile: 'ProAm profile',
  account_setup: 'Account setup',
  leaderboard: 'Leaderboard',
  sessions: 'Sessions',
  highlights: 'Highlights',
  workout_completion: 'Workout completion',
  calorie_tracker: 'Calorie Tracker',
};

const formatDateTime = (value) => {
  if (!value) return 'Not set';
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return String(value);
  }
};

const formatMoney = (cents, currency = 'eur') => {
  const amount = Number(cents || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: String(currency || 'eur').toUpperCase(),
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency || ''}`.trim();
  }
};

const countBy = (rows, keyFn) => {
  const counts = new Map();
  rows.forEach((row) => {
    const key = keyFn(row) || 'unknown';
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
};

const formatAnalyticsLabel = (value) => {
  if (EVENT_LABELS[value]) return EVENT_LABELS[value];
  if (String(value || '').startsWith('meta_')) return `Meta · ${formatAnalyticsLabel(String(value).slice(5))}`;
  const normalized = String(value || 'unknown').replace(/_/g, ' ').trim();
  if (!normalized) return 'Unknown';
  return normalized.replace(/\b\w/g, (character) => character.toUpperCase());
};

const formatScreenLabel = (value) => {
  if (SCREEN_LABELS[value]) return SCREEN_LABELS[value];
  if (value === 'Fitness') return 'Tracked Steps';
  return String(value || 'unknown')
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const profileLabel = (profile, fallback) => {
  if (!profile) return 'Unknown user';
  if (profile.username) return `@${profile.username}`;
  if (profile.email) return profile.email;
  return fallback || 'Unknown user';
};

function AnalyticsAvatar({ profile, label, size = 'small' }) {
  const [imageFailed, setImageFailed] = React.useState(false);
  const initials = String(label || '?')
    .replace(/^@/, '')
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || '?';
  const imageUrl = profile?.profile_img;

  return (
    <span className={`analytics-avatar analytics-avatar-${size}`} aria-hidden="true">
      {imageUrl && !imageFailed ? (
        <img src={imageUrl} alt="" onError={() => setImageFailed(true)} />
      ) : initials}
    </span>
  );
}

function AnalyticsUserIdentity({ profile, fallback, size = 'small' }) {
  const label = profileLabel(profile, fallback);
  return (
    <span className={`analytics-user-identity analytics-user-identity-${size}`}>
      <AnalyticsAvatar profile={profile} label={label} size={size} />
      <span>{label}</span>
    </span>
  );
}

const csvEscape = (value) => {
  const normalized = value === null || value === undefined ? '' : String(value);
  if (!/[",\n]/.test(normalized)) return normalized;
  return `"${normalized.replace(/"/g, '""')}"`;
};

export default function AppAnalyticsDashboard() {
  const { user, profile, loading: authLoading } = useAuth();
  const [fromDate, setFromDate] = React.useState(todayInputValue());
  const [toDate, setToDate] = React.useState(todayInputValue());
  const [checkingAdmin, setCheckingAdmin] = React.useState(true);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [events, setEvents] = React.useState([]);
  const [sourceFilter, setSourceFilter] = React.useState('all');
  const [signupDays, setSignupDays] = React.useState([]);
  const [profilesById, setProfilesById] = React.useState({});
  const [payments, setPayments] = React.useState([]);
  const [eventCount, setEventCount] = React.useState(0);
  const [paymentCount, setPaymentCount] = React.useState(0);
  const [selectedProfileId, setSelectedProfileId] = React.useState(null);
  const [trendMode, setTrendMode] = React.useState('signups');
  const [updatedAt, setUpdatedAt] = React.useState(null);
  const [activeDetailTab, setActiveDetailTab] = React.useState('signups');
  const [signupPage, setSignupPage] = React.useState(0);
  const [signupProfiles, setSignupProfiles] = React.useState([]);
  const [signupListLoading, setSignupListLoading] = React.useState(false);
  const [signupListError, setSignupListError] = React.useState('');
  const loadRequestId = React.useRef(0);
  const signupRequestId = React.useRef(0);
  const userDetailRef = React.useRef(null);

  React.useEffect(() => {
    let mounted = true;
    const check = async () => {
      if (authLoading) return;
      if (!user?.id) {
        setIsAdmin(false);
        setCheckingAdmin(false);
        return;
      }
      const email = String(user.email || profile?.email || '').toLowerCase();
      if (email === 'admin@snowsproam.com') {
        setIsAdmin(true);
        setCheckingAdmin(false);
        return;
      }
      try {
        const { data, error: rpcError } = await supabase.rpc('is_platform_admin', { p_profile_id: user.id });
        if (rpcError) throw rpcError;
        if (mounted) setIsAdmin(!!data);
      } catch {
        if (mounted) setIsAdmin(false);
      } finally {
        if (mounted) setCheckingAdmin(false);
      }
    };
    check();
    return () => {
      mounted = false;
    };
  }, [authLoading, profile?.email, user?.email, user?.id]);

  const loadAnalytics = React.useCallback(async () => {
    if (!isAdmin) return;
    const requestId = ++loadRequestId.current;
    setLoading(true);
    setError('');

    try {
      const { startIso, endExclusiveIso } = analyticsDateRange(fromDate, toDate);
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      let eventsQuery = supabase
        .from('app_analytics_events')
        .select('id,profile_id,session_id,event_name,event_category,screen,source,target_type,target_id,sport,platform,app_environment,occurred_at', { count: 'exact' })
        .gte('occurred_at', startIso)
        .lt('occurred_at', endExclusiveIso);
      if (sourceFilter === 'meta') eventsQuery = eventsQuery.like('event_name', 'meta_%');
      if (sourceFilter === 'app') eventsQuery = eventsQuery.not('event_name', 'like', 'meta_%');
      const [eventsRes, paymentsRes, signupsRes] = await Promise.all([
        eventsQuery.order('occurred_at', { ascending: false }).limit(EVENT_LIMIT),
        supabase
          .from('billing_payments')
          .select('id,profile_id,amount_cents,currency,status,target_type,target_id,created_at', { count: 'exact' })
          .gte('created_at', startIso)
          .lt('created_at', endExclusiveIso)
          .order('created_at', { ascending: false })
          .limit(2000),
        supabase.rpc('admin_app_signup_counts', {
          p_start: startIso,
          p_end_exclusive: endExclusiveIso,
          p_timezone: timeZone,
        }),
      ]);

      if (eventsRes.error) throw eventsRes.error;
      if (paymentsRes.error) throw paymentsRes.error;
      if (signupsRes.error) throw signupsRes.error;
      if (requestId !== loadRequestId.current) return;

      const rows = (eventsRes.data || []).filter(
        (event) => !EXCLUDED_ANALYTICS_EVENTS.has(event.event_name),
      );
      const profileIds = Array.from(new Set(rows.map((row) => row.profile_id).filter(Boolean)));
      let profileMap = {};
      if (profileIds.length) {
        const batches = [];
        for (let index = 0; index < profileIds.length; index += 200) {
          batches.push(profileIds.slice(index, index + 200));
        }
        const profileResults = await Promise.all(batches.map((batch) => supabase
          .from('profiles')
          .select('id,email,username,profile_img,country,current_country,device_type,created_at')
          .in('id', batch)));
        profileResults.forEach(({ data, error: profileError }) => {
          if (profileError) throw profileError;
          (data || []).forEach((row) => {
            profileMap[row.id] = row;
          });
        });
      }
      if (requestId !== loadRequestId.current) return;

      setEvents(rows);
      setSignupDays(signupsRes.data || []);
      setProfilesById(profileMap);
      setPayments(paymentsRes.data || []);
      setEventCount(eventsRes.count ?? eventsRes.data?.length ?? 0);
      setPaymentCount(paymentsRes.count ?? paymentsRes.data?.length ?? 0);
      setUpdatedAt(new Date());
      setSelectedProfileId((current) => (current && !profileIds.includes(current) ? null : current));
    } catch (loadError) {
      if (requestId !== loadRequestId.current) return;
      setError(loadError?.message || 'Analytics data could not be loaded.');
      setEvents([]);
      setSignupDays([]);
      setProfilesById({});
      setPayments([]);
      setEventCount(0);
      setPaymentCount(0);
      setSelectedProfileId(null);
    } finally {
      if (requestId === loadRequestId.current) setLoading(false);
    }
  }, [fromDate, isAdmin, sourceFilter, toDate]);

  React.useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const loadSignupProfiles = React.useCallback(async () => {
    if (!isAdmin) return;
    const requestId = ++signupRequestId.current;
    setSignupListLoading(true);
    setSignupListError('');
    setSignupProfiles([]);
    try {
      const { startIso, endExclusiveIso } = analyticsDateRange(fromDate, toDate);
      const { data, error: listError } = await supabase.rpc('admin_app_signup_profiles', {
        p_start: startIso,
        p_end_exclusive: endExclusiveIso,
        p_limit: SIGNUP_PAGE_SIZE,
        p_offset: signupPage * SIGNUP_PAGE_SIZE,
      });
      if (listError) throw listError;
      if (requestId === signupRequestId.current) setSignupProfiles(data || []);
    } catch (listError) {
      if (requestId === signupRequestId.current) setSignupListError(listError?.message || 'Signup details could not be loaded.');
    } finally {
      if (requestId === signupRequestId.current) setSignupListLoading(false);
    }
  }, [fromDate, isAdmin, signupPage, toDate]);

  React.useEffect(() => {
    loadSignupProfiles();
  }, [loadSignupProfiles]);

  const metrics = React.useMemo(() => {
    const summary = summarizeAnalytics(events, signupDays, payments);
    return [
      { label: 'Completed signups', value: summary.signups.toLocaleString(), hint: 'New profiles with a username', icon: TrendingUp, tone: 'growth' },
      { label: 'Engaged users', value: summary.activeUsers.toLocaleString(), hint: 'Unique identified users with tracked events', icon: Users, tone: 'engagement' },
      { label: 'Tracked events', value: summary.actions.toLocaleString(), hint: 'Recorded app and Meta signals', icon: Activity, tone: 'activity' },
      { label: 'Joins and entries', value: summary.otherConversions.toLocaleString(), hint: 'Recorded participation events', icon: BarChart3, tone: 'participation' },
    ];
  }, [events, signupDays, payments]);

  const chartData = React.useMemo(
    () => buildDailyTrend(events, signupDays, fromDate, toDate),
    [events, signupDays, fromDate, toDate],
  );
  const dateRangeInvalid = !fromDate || !toDate || fromDate > toDate;

  const meaningfulEvents = events;
  const topScreens = React.useMemo(() => countBy(events, (event) => formatScreenLabel(event.screen)).slice(0, 8), [events]);
  const topActions = React.useMemo(() => {
    const usersByEvent = new Map();
    meaningfulEvents.forEach((event) => {
      if (!event.profile_id) return;
      const users = usersByEvent.get(event.event_name) || new Set();
      users.add(event.profile_id);
      usersByEvent.set(event.event_name, users);
    });
    return countBy(meaningfulEvents, (event) => event.event_name).slice(0, 10).map((row) => ({
      ...row,
      name: formatAnalyticsLabel(row.name),
      meta: `${(usersByEvent.get(row.name)?.size || 0).toLocaleString()} ${(usersByEvent.get(row.name)?.size || 0) === 1 ? 'user' : 'users'}`,
    }));
  }, [meaningfulEvents]);
  const topUsers = React.useMemo(() => countBy(events.filter((event) => event.profile_id), (event) => event.profile_id).slice(0, 10), [events]);
  const eventCategoryBreakdown = React.useMemo(
    () => countBy(meaningfulEvents, (event) => formatAnalyticsLabel(event.event_category)),
    [meaningfulEvents],
  );
  const platformBreakdown = React.useMemo(
    () => countBy(events, (event) => String(event.platform || 'unknown').toUpperCase()),
    [events],
  );
  const selectedUserEvents = React.useMemo(
    () => events.filter((event) => event.profile_id === selectedProfileId),
    [events, selectedProfileId],
  );
  const selectedUserPayments = React.useMemo(
    () => payments.filter((payment) => payment.profile_id === selectedProfileId),
    [payments, selectedProfileId],
  );
  const paymentAmounts = React.useMemo(() => Array.from(summarizeAnalytics([], [], payments).paymentTotals.entries()), [payments]);
  const signupTotal = signupDays.reduce((sum, day) => sum + Number(day.signups || 0), 0);
  const partialEvents = eventCount > EVENT_LIMIT;
  const partialPayments = paymentCount > 2000;
  const peakDay = React.useMemo(() => chartData.reduce((best, day) => day.signups > (best?.signups || 0) ? day : best, null), [chartData]);
  const averageSignups = chartData.length ? chartData.reduce((sum, day) => sum + day.signups, 0) / chartData.length : 0;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const choosePreset = (days) => {
    const end = new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - days + 1);
    setFromDate(localDateKey(start));
    setToDate(localDateKey(end));
    setSignupPage(0);
  };
  const selectUser = (id) => {
    setSelectedProfileId(id);
    setActiveDetailTab('people');
    window.requestAnimationFrame(() => {
      userDetailRef.current?.focus({ preventScroll: true });
      userDetailRef.current?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  };

  const exportCsv = () => {
    const header = ['occurred_at', 'event_name', 'event_category', 'profile_id', 'screen', 'source', 'target_type', 'target_id', 'sport', 'platform', 'app_environment'];
    const body = events.map((event) => header.map((key) => csvEscape(event[key])).join(','));
    const blob = new Blob([[header.join(','), ...body].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `snowsproam-app-analytics-${fromDate}-to-${toDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (authLoading || checkingAdmin) {
    return (
      <main className="analytics-admin-shell">
        <style>{analyticsCss}</style>
        <div className="analytics-state" role="status">Checking admin access.</div>
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="analytics-admin-shell">
        <style>{analyticsCss}</style>
        <div className="analytics-state denied" role="alert">
          <AlertTriangle size={22} aria-hidden="true" />
          <h1>Admin access required</h1>
          <p>This analytics dashboard is only available to platform admin accounts.</p>
          <Link className="analytics-secondary" to="/">Back to home</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="analytics-admin-shell">
      <style>{analyticsCss}</style>
      <header className="analytics-header">
        <div>
          <Link className="analytics-back" to="/admin">
            <ArrowLeft size={16} aria-hidden="true" />
            Admin dashboard
          </Link>
          <span className="analytics-eyebrow">Product intelligence</span>
          <h1>App analytics</h1>
          <p>Understand new registrations, app interactions, and mirrored Meta events.</p>
          <p className="analytics-data-note">Dates in {timeZone} · {updatedAt ? `Updated ${formatDateTime(updatedAt)}` : 'Waiting for data'}</p>
        </div>
        <div className="analytics-actions" aria-label="Analytics filters">
          <div className="analytics-presets" aria-label="Quick date ranges">
            <button type="button" onClick={() => choosePreset(1)}>Today</button>
            <button type="button" onClick={() => choosePreset(7)}>7 days</button>
            <button type="button" onClick={() => choosePreset(30)}>30 days</button>
          </div>
          <label>
            <span>From</span>
            <input type="date" value={fromDate} max={toDate} aria-invalid={dateRangeInvalid} aria-describedby={dateRangeInvalid ? 'analytics-date-error' : undefined} onChange={(event) => { setFromDate(event.target.value); setSignupPage(0); }} />
          </label>
          <label>
            <span>To</span>
            <input type="date" value={toDate} min={fromDate} max={todayInputValue()} aria-invalid={dateRangeInvalid} aria-describedby={dateRangeInvalid ? 'analytics-date-error' : undefined} onChange={(event) => { setToDate(event.target.value); setSignupPage(0); }} />
          </label>
          {dateRangeInvalid ? <span id="analytics-date-error" className="analytics-date-error">Choose both dates in order.</span> : null}
          <button type="button" className="analytics-secondary" onClick={loadAnalytics} disabled={loading || dateRangeInvalid}>
            <RefreshCcw size={16} aria-hidden="true" className={loading ? 'spin' : ''} />
            Refresh
          </button>
          <button type="button" className="analytics-primary" onClick={exportCsv} disabled={loading || !!error || !events.length}>
            <Download size={16} aria-hidden="true" />
            Export events CSV
          </button>
        </div>
      </header>

      <div className="analytics-source-filters" role="group" aria-label="Event source">
        <span>Event source</span>
        {[
          ['all', 'All events'],
          ['meta', 'Meta events'],
          ['app', 'App events'],
        ].map(([value, label]) => (
          <button key={value} type="button" aria-pressed={sourceFilter === value} onClick={() => {
            setSourceFilter(value);
            if (value === 'meta') setActiveDetailTab('events');
          }}>{label}</button>
        ))}
        <small>Signup and payment totals follow the selected dates.</small>
      </div>

      {error ? (
        <div className="analytics-alert" role="alert">
          <AlertTriangle size={18} aria-hidden="true" />
          {error}
        </div>
      ) : null}

      {!loading && !error && (partialEvents || partialPayments) ? (
        <div className="analytics-alert" role="status">
          <AlertTriangle size={18} aria-hidden="true" />
          {partialEvents ? 'Event-based figures and charts show the latest 5,000 raw events only. ' : ''}
          {partialPayments ? 'Payments show the latest 2,000 records only. ' : ''}
          Choose a shorter range for complete event or payment totals. Completed signup counts remain complete.
        </div>
      ) : null}

      <section className="analytics-metrics" aria-label="Growth and engagement summary" aria-busy={loading}>
        {metrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <article key={metric.label} className={`analytics-metric analytics-metric-${metric.tone}`}>
              <span className="analytics-metric-icon"><Icon size={20} aria-hidden="true" /></span>
              <span>{metric.label}</span>
              <strong>{loading || error ? '—' : metric.value}</strong>
              <small>{metric.hint}</small>
            </article>
          );
        })}
      </section>

      {error ? (
        <section className="analytics-panel analytics-failed" aria-label="Analytics unavailable">
          <h2>Analytics could not be loaded</h2>
          <p>Try Refresh. No figures are shown until all sources load successfully.</p>
          <button type="button" className="analytics-primary" onClick={loadAnalytics}>Retry loading</button>
        </section>
      ) : <>
      <section className="analytics-growth-layout" aria-label="Signup growth">
        <div className="analytics-panel analytics-chart-panel">
          <div className="analytics-panel-head analytics-trend-head">
            <div><span className="analytics-section-kicker">Growth over time</span><h2 id="analytics-trend-title">Daily {trendMode === 'signups' ? 'completed signups' : 'engaged users'}</h2><p>{trendMode === 'signups' ? 'Profiles completed each day' : 'Unique identified users with tracked events each day'}</p></div>
            <div className="analytics-trend-switch" aria-label="Trend metric">
              <button type="button" aria-pressed={trendMode === 'signups'} onClick={() => setTrendMode('signups')}>Signups</button>
              <button type="button" aria-pressed={trendMode === 'users'} onClick={() => setTrendMode('users')}>Engaged users</button>
            </div>
          </div>
          {loading ? <div className="analytics-state" role="status">Loading growth trend.</div> : chartData.some((day) => day[trendMode === 'signups' ? 'signups' : 'users']) ? (
            <div className="analytics-growth-chart" role="img" aria-label={`${trendMode === 'signups' ? 'Completed signups' : 'Engaged users'} by day: ${chartData.map((day) => `${day.fullDate} ${day[trendMode === 'signups' ? 'signups' : 'users']}`).join(', ')}`}>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData} margin={{ top: 15, right: 12, bottom: 0, left: -18 }}>
                  <defs><linearGradient id="analyticsTrendFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={trendMode === 'signups' ? '#1377a8' : '#16a79e'} stopOpacity={0.27} /><stop offset="100%" stopColor={trendMode === 'signups' ? '#1377a8' : '#16a79e'} stopOpacity={0.015} /></linearGradient></defs>
                  <CartesianGrid stroke="#e8eef4" strokeDasharray="3 5" vertical={false} />
                  <XAxis dataKey="date" tickLine={false} axisLine={false} minTickGap={24} tick={{ fill: '#64748b', fontSize: 12 }} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                  <Tooltip labelFormatter={(label, payload) => payload?.[0]?.payload?.fullDate || label} formatter={(value) => [value, trendMode === 'signups' ? 'Completed signups' : 'Engaged users']} />
                  <Area dataKey={trendMode === 'signups' ? 'signups' : 'users'} type="monotone" stroke={trendMode === 'signups' ? '#1377a8' : '#16a79e'} strokeWidth={3} fill="url(#analyticsTrendFill)" dot={chartData.length <= 7} activeDot={{ r: 6 }} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : <div className="analytics-state">No {trendMode === 'signups' ? 'completed signups' : 'tracked engaged users'} in this range.</div>}
          <details className="analytics-chart-data"><summary>View daily numbers</summary><div className="analytics-table-wrap" tabIndex="0"><table className="analytics-table"><thead><tr><th>Date</th><th>Signups</th><th>Engaged users</th><th>Tracked events</th></tr></thead><tbody>{chartData.map((day) => <tr key={day.fullDate}><td>{day.fullDate}</td><td>{day.signups}</td><td>{day.users}</td><td>{day.actions}</td></tr>)}</tbody></table></div></details>
        </div>
        <aside className="analytics-panel analytics-insights" aria-label="Growth observations">
          <div className="analytics-panel-head"><div><span className="analytics-section-kicker">At a glance</span><h2>This range in numbers</h2></div></div>
          {loading ? <div className="analytics-state compact" role="status">Loading observations.</div> : <div className="analytics-insight-list">
            <div><span>Highest signup day</span><strong>{peakDay?.signups ? peakDay.signups.toLocaleString() : '—'}</strong><small>{peakDay?.signups ? peakDay.fullDate : 'No signups recorded'}</small></div>
            <div><span>Average per day</span><strong>{averageSignups.toLocaleString(undefined, { maximumFractionDigits: 1 })}</strong><small>Completed signups over {chartData.length.toLocaleString()} calendar {chartData.length === 1 ? 'day' : 'days'}</small></div>
            <div><span>Participation</span><strong>{summarizeAnalytics(events, signupDays, payments).otherConversions.toLocaleString()}</strong><small>Tracked joins and entries; separate from signups</small></div>
          </div>}
        </aside>
      </section>

      <nav className="analytics-detail-tabs" aria-label="Explore analytics details">
        {[
          ['signups', 'Signups'],
          ['features', 'Feature usage'],
          ['people', 'People'],
          ['payments', 'Payments'],
          ['events', 'Event log'],
        ].map(([id, label]) => <button key={id} type="button" aria-pressed={activeDetailTab === id} onClick={() => setActiveDetailTab(id)}>{label}</button>)}
      </nav>

      {activeDetailTab === 'signups' ? (
      <section className="analytics-panel analytics-signup-panel" aria-labelledby="analytics-signups-title">
        <div className="analytics-panel-head analytics-signup-head">
          <div><span className="analytics-section-kicker">New accounts</span><h2 id="analytics-signups-title">Who signed up</h2><p>Completed profiles created in the selected dates, newest first</p></div>
          <strong>{loading ? '—' : signupTotal.toLocaleString()} signups</strong>
        </div>
        {signupListLoading || loading ? <div className="analytics-state compact" role="status">Loading signup details.</div> : signupListError ? (
          <div className="analytics-signup-error" role="alert"><p>{signupListError}</p><button type="button" className="analytics-secondary" onClick={loadSignupProfiles}>Retry signup list</button></div>
        ) : signupProfiles.length ? <>
          <div className="analytics-table-wrap" tabIndex="0" aria-label="Signup list, scroll horizontally on small screens">
            <table className="analytics-table analytics-signup-table">
              <thead><tr><th>Registered</th><th>Username</th><th>Email</th></tr></thead>
              <tbody>{signupProfiles.map((signup) => <tr key={signup.profile_id}><td>{formatDateTime(signup.created_at)}</td><td><strong>@{signup.username}</strong></td><td>{signup.email || 'No email recorded'}</td></tr>)}</tbody>
            </table>
          </div>
          <div className="analytics-signup-pagination">
            <span>Showing {(signupPage * SIGNUP_PAGE_SIZE + 1).toLocaleString()}–{(signupPage * SIGNUP_PAGE_SIZE + signupProfiles.length).toLocaleString()} of {signupTotal.toLocaleString()}</span>
            <div><button type="button" className="analytics-secondary" onClick={() => setSignupPage((page) => Math.max(0, page - 1))} disabled={signupPage === 0}>Previous</button><button type="button" className="analytics-secondary" onClick={() => setSignupPage((page) => page + 1)} disabled={(signupPage + 1) * SIGNUP_PAGE_SIZE >= signupTotal}>Next</button></div>
          </div>
        </> : <div className="analytics-state compact">No completed signups in this date range.</div>}
      </section>
      ) : null}

      {activeDetailTab === 'features' ? <>
      <section className="analytics-grid analytics-engagement-grid" aria-label="Engagement breakdown">
        <AnalyticsList
          title="Which actions get used"
          description="Tracked events and unique identified users per action"
          rows={topActions}
          loading={loading}
          emptyMessage="No tracked events in this date range."
        />
        <AnalyticsDonutChart
          title="Platform mix"
          description="Share of tracked events by device, not share of users"
          rows={platformBreakdown}
          loading={loading}
          colorFor={(name) => PLATFORM_COLORS[name] || PLATFORM_COLORS.UNKNOWN}
        />
      </section>

      <section className="analytics-visual-grid" aria-label="Where interactions happen">
        <AnalyticsScreenChart rows={topScreens} loading={loading} />
        <AnalyticsDonutChart
          title="Interaction types"
          description="Categories of tracked events"
          rows={eventCategoryBreakdown}
          loading={loading}
        />
      </section>
      </> : null}

      {activeDetailTab === 'people' ? <>
      <section className="analytics-grid analytics-investigation-grid" aria-label="Explore user activity">
        <AnalyticsPlayerRoster
          rows={topUsers.map((row) => ({
            ...row,
            id: row.name,
            name: profileLabel(profilesById[row.name], row.name),
            profile: profilesById[row.name],
          }))}
          onRowClick={(row) => selectUser(row.id)}
          selectedRowId={selectedProfileId}
          loading={loading}
        />
      </section>

      {!dateRangeInvalid && selectedProfileId && selectedUserEvents.length ? (
        <div ref={userDetailRef} className="analytics-detail-anchor" tabIndex="-1"><AnalyticsUserDetail
          profile={profilesById[selectedProfileId]}
          profileId={selectedProfileId}
          events={selectedUserEvents}
          payments={selectedUserPayments}
          fromDate={fromDate}
          toDate={toDate}
          onClose={() => setSelectedProfileId(null)}
        /></div>
      ) : null}
      </> : null}

      {activeDetailTab === 'payments' ? <section className="analytics-panel analytics-payment-panel analytics-payment-tab" aria-labelledby="analytics-payments-title">
        <div className="analytics-panel-head"><div><h2 id="analytics-payments-title">Payments</h2><p>Succeeded money payments created in this range</p></div><CalendarDays size={20} aria-hidden="true" /></div>
        {loading ? <div className="analytics-state compact" role="status">Loading payments.</div> : <div className="analytics-payment-value">{paymentAmounts.length ? paymentAmounts.map(([currency, cents]) => <strong key={currency}>{formatMoney(cents, currency)}</strong>) : <strong>{formatMoney(0)}</strong>}<small>{partialPayments ? 'Partial payment data' : 'Pending, failed and nonmoney transactions excluded'}</small></div>}
      </section> : null}

      {activeDetailTab === 'events' ? (
      <section className="analytics-panel" aria-labelledby="recent-events-title">
        <div className="analytics-panel-head">
          <div><h2 id="recent-events-title">Recent event log</h2><p>Inspect the latest recorded interactions</p></div>
          <span>{events.length.toLocaleString()} loaded</span>
        </div>
        {loading ? <div className="analytics-state compact" role="status">Loading events.</div> : <div className="analytics-table-wrap" tabIndex="0" aria-label="Recent events table, scroll horizontally on small screens">
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Event</th>
                <th>User</th>
                <th>Screen</th>
                <th>Target</th>
                <th>Platform</th>
              </tr>
            </thead>
            <tbody>
              {events.slice(0, 80).map((event) => (
                <tr key={event.id}>
                  <td>{formatDateTime(event.occurred_at)}</td>
                  <td><strong>{formatAnalyticsLabel(event.event_name)}</strong></td>
                  <td>
                    {event.profile_id ? (
                      <button
                        type="button"
                        className="analytics-user-link"
                        onClick={() => selectUser(event.profile_id)}
                      >
                        <AnalyticsUserIdentity
                          profile={profilesById[event.profile_id]}
                          fallback={event.profile_id}
                        />
                      </button>
                    ) : 'Unknown user'}
                  </td>
                  <td>{formatScreenLabel(event.screen)}</td>
                  <td>{[event.target_type, event.target_id].filter(Boolean).join(' / ') || 'None'}</td>
                  <td>{[event.platform, event.app_environment].filter(Boolean).join(' / ') || 'unknown'}</td>
                </tr>
              ))}
              {!events.length && !loading ? (
                <tr>
                  <td colSpan="6">No events found for this range.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>}
      </section>
      ) : null}
      </>}
    </main>
  );
}

function AnalyticsScreenChart({ rows, loading }) {
  const accessibleSummary = rows.map((row) => `${row.name}: ${row.count}`).join(', ');
  return (
    <section className="analytics-panel analytics-screen-chart" aria-labelledby="screen-usage-title">
      <div className="analytics-panel-head">
        <div>
          <h2 id="screen-usage-title">Where events happen</h2>
          <p>App areas connected to tracked events</p>
        </div>
      </div>
      {loading ? (
        <div className="analytics-state compact" role="status">Loading screen usage.</div>
      ) : rows.length ? (
        <div className="analytics-chart-content" role="img" aria-label={`Event locations: ${accessibleSummary}`}>
          <ResponsiveContainer width="100%" height={238}>
            <BarChart data={rows} layout="vertical" margin={{ top: 6, right: 20, bottom: 4, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false} />
              <YAxis type="category" dataKey="name" width={96} tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value) => [`${value} events`, 'Events']} />
              <Bar dataKey="count" fill="#2563eb" radius={[0, 5, 5, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="analytics-state compact">No action locations in this date range yet.</div>
      )}
    </section>
  );
}

function AnalyticsDonutChart({ title, description, rows, loading, colorFor }) {
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  const accessibleSummary = rows.map((row) => `${row.name}: ${row.count}`).join(', ');
  const titleId = `${title.replace(/\s+/g, '-').toLowerCase()}-title`;
  return (
    <section className="analytics-panel analytics-donut-chart" aria-labelledby={titleId}>
      <div className="analytics-panel-head">
        <div>
          <h2 id={titleId}>{title}</h2>
          <p>{description}</p>
        </div>
      </div>
      {loading ? (
        <div className="analytics-state compact" role="status">Loading chart.</div>
      ) : rows.length ? (
        <>
          <div className="analytics-chart-content analytics-donut-canvas" role="img" aria-label={`${title}: ${accessibleSummary}`}>
            <ResponsiveContainer width="100%" height={168}>
              <PieChart>
                <Pie data={rows} dataKey="count" nameKey="name" innerRadius={45} outerRadius={67} paddingAngle={2} stroke="none">
                  {rows.map((row, index) => <Cell key={row.name} fill={colorFor?.(row.name) || CHART_COLORS[index % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(value) => [`${value} events`, 'Events']} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="analytics-chart-key" aria-label={`${title} details`}>
            {rows.map((row, index) => (
              <li key={row.name}>
                <span><i style={{ backgroundColor: colorFor?.(row.name) || CHART_COLORS[index % CHART_COLORS.length] }} aria-hidden="true" />{row.name}</span>
                <strong>{row.count.toLocaleString()} <small>{total ? ((row.count / total) * 100 < 1 ? '<1%' : `${Math.round((row.count / total) * 100)}%`) : ''}</small></strong>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className="analytics-state compact">No data in this date range yet.</div>
      )}
    </section>
  );
}

function AnalyticsList({ title, description, rows, loading, emptyMessage = 'No data yet.' }) {
  const max = rows[0]?.count || 1;
  return (
    <section className="analytics-panel" aria-labelledby={`${title.replace(/\s+/g, '-').toLowerCase()}-title`}>
      <div className="analytics-panel-head">
        <div><h2 id={`${title.replace(/\s+/g, '-').toLowerCase()}-title`}>{title}</h2>{description ? <p>{description}</p> : null}</div>
      </div>
      <div className="analytics-list">
        {loading ? <div className="analytics-state compact" role="status">Loading {title.toLowerCase()}.</div> : rows.length ? rows.map((row, index) => <div key={`${row.name}-${index}`} className="analytics-ranked-row"><div className="analytics-ranked-heading"><strong>{row.name}</strong><span>{row.count.toLocaleString()} {row.count === 1 ? 'event' : 'events'} · {row.meta}</span></div><div className="analytics-ranked-track" aria-hidden="true"><span style={{ width: `${row.count / max * 100}%` }} /></div></div>) : <div className="analytics-state compact">{emptyMessage}</div>}
      </div>
    </section>
  );
}

function AnalyticsPlayerRoster({ rows, onRowClick, selectedRowId, loading }) {
  return (
    <section className="analytics-panel analytics-player-roster" aria-labelledby="active-athletes-title">
      <div className="analytics-panel-head">
        <div>
          <h2 id="active-athletes-title">Athletes with events</h2>
          <p>Choose a player to understand their journey</p>
        </div>
      </div>
      {loading ? <div className="analytics-state compact" role="status">Loading active athletes.</div> : rows.length ? (
        <div className="analytics-player-grid">
          {rows.map((row) => (
            <button
              key={row.id}
              type="button"
              className="analytics-player-card"
              onClick={() => onRowClick(row)}
              aria-pressed={selectedRowId === row.id}
              aria-label={`View activity for ${row.name}`}
            >
              <AnalyticsAvatar profile={row.profile} label={row.name} size="small" />
              <span className="analytics-player-name">{row.name}</span>
              <span className="analytics-player-count">{row.count.toLocaleString()} <small>events</small></span>
              <span className="analytics-player-track" aria-hidden="true"><i style={{ width: `${row.count / (rows[0]?.count || 1) * 100}%` }} /></span>
            </button>
          ))}
        </div>
      ) : <div className="analytics-state compact">No active athletes in this date range.</div>}
    </section>
  );
}

function AnalyticsUserDetail({ profile, profileId, events, payments, fromDate, toDate, onClose }) {
  const displayName = profileLabel(profile, profileId);
  const actionTypeCount = new Set(events.map((event) => event.event_name)).size;
  const sessions = new Set(events.map((event) => event.session_id).filter(Boolean)).size;
  const { startIso, endExclusiveIso } = analyticsDateRange(fromDate, toDate);
  const profileCreatedAt = profile?.created_at ? new Date(profile.created_at).getTime() : NaN;
  const joinedInRange = !!profile?.username
    && profileCreatedAt >= new Date(startIso).getTime()
    && profileCreatedAt < new Date(endExclusiveIso).getTime();
  const participationEvents = events.filter((event) => isJoinOrEntryEvent(event.event_name)).length;
  const paymentTotals = summarizeAnalytics([], [], payments).paymentTotals;
  const paidAmount = paymentTotals.size
    ? Array.from(paymentTotals.entries()).map(([currency, cents]) => formatMoney(cents, currency)).join(' + ')
    : formatMoney(0);
  const topScreens = countBy(events, (event) => formatScreenLabel(event.screen)).slice(0, 5);
  const topActions = countBy(events, (event) => formatAnalyticsLabel(event.event_name)).slice(0, 5);
  const lastEvent = events[0];
  const firstEvent = events[events.length - 1];

  return (
    <section className="analytics-panel analytics-user-detail" aria-labelledby="user-activity-title">
      <div className="analytics-panel-head analytics-user-detail-head">
        <div className="analytics-player-heading">
          <AnalyticsAvatar profile={profile} label={displayName} size="hero" />
          <div>
            <span>Player activity</span>
            <h2 id="user-activity-title">{displayName}</h2>
          </div>
          <p>{fromDate} to {toDate} · {profile?.created_at ? `Joined ${new Date(profile.created_at).toLocaleDateString()}` : 'Profile join date unavailable'}</p>
        </div>
        <button type="button" className="analytics-icon-button" onClick={onClose} aria-label="Close user activity details">
          <X size={18} aria-hidden="true" />
        </button>
      </div>

      <div className="analytics-user-metrics" aria-label={`${displayName} activity summary`}>
        <div><span>Tracked events</span><strong>{events.length.toLocaleString()}</strong></div>
        <div><span>Event types</span><strong>{actionTypeCount.toLocaleString()}</strong></div>
        <div><span>Sessions</span><strong>{sessions.toLocaleString()}</strong></div>
        <div><span>Joins / entries</span><strong>{participationEvents.toLocaleString()}</strong></div>
        <div><span>Payments</span><strong>{paidAmount}</strong></div>
      </div>
      {joinedInRange ? <p className="analytics-user-joined">Completed signup in this date range</p> : null}

      <div className="analytics-user-insights">
        <div>
          <h3>Where events happened</h3>
          <AnalyticsCompactList rows={topScreens} emptyMessage="No event locations in this range." />
        </div>
        <div>
          <h3>Top events</h3>
          <AnalyticsCompactList rows={topActions} emptyMessage="No events in this date range." />
        </div>
        <div>
          <h3>Activity window</h3>
          <dl className="analytics-user-window">
            <div><dt>First activity</dt><dd>{formatDateTime(firstEvent?.occurred_at)}</dd></div>
            <div><dt>Latest activity</dt><dd>{formatDateTime(lastEvent?.occurred_at)}</dd></div>
            <div><dt>Device</dt><dd>{[lastEvent?.platform, lastEvent?.app_environment].filter(Boolean).join(' / ') || 'Unknown'}</dd></div>
          </dl>
        </div>
      </div>

    </section>
  );
}

function AnalyticsCompactList({ rows, emptyMessage }) {
  return rows.length ? (
    <ul className="analytics-compact-list">
      {rows.map((row) => <li key={row.name}><span>{row.name}</span><strong>{row.count.toLocaleString()}</strong></li>)}
    </ul>
  ) : <p className="analytics-empty-copy">{emptyMessage}</p>;
}

const analyticsCss = `
.analytics-admin-shell{min-height:100vh;background:#f5f7fb;color:#101828;padding:24px;font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}
.analytics-header{max-width:1360px;margin:0 auto 16px;display:flex;align-items:flex-end;justify-content:space-between;gap:16px}
.analytics-header h1{margin:8px 0 4px;font-size:30px;line-height:1.1;letter-spacing:0;color:#101828}
.analytics-header p{margin:0;color:#667085;font-size:14px;line-height:1.4}
.analytics-back{display:inline-flex;align-items:center;gap:6px;color:#2563eb;font-weight:800;font-size:13px;text-decoration:none}
.analytics-actions{display:flex;align-items:flex-end;gap:10px;flex-wrap:wrap;justify-content:flex-end}
.analytics-actions label{display:grid;gap:5px;color:#475467;font-size:12px;font-weight:800}
.analytics-actions input{height:38px;border:1px solid #cfd8e3;border-radius:8px;padding:0 10px;background:#fff;color:#101828;font-size:14px}
.analytics-actions input[aria-invalid=true]{border-color:#c2410c}.analytics-date-error{color:#9a3412;font-size:12px;font-weight:750}
.analytics-primary,.analytics-secondary{min-height:38px;border-radius:8px;border:1px solid transparent;padding:0 12px;display:inline-flex;align-items:center;justify-content:center;gap:7px;font-weight:850;cursor:pointer;text-decoration:none}
.analytics-primary{background:#2563eb;color:#fff}
.analytics-secondary{background:#fff;color:#344054;border-color:#cfd8e3}
.analytics-primary:disabled,.analytics-secondary:disabled{opacity:.55;cursor:not-allowed}
.analytics-alert{max-width:1360px;margin:0 auto 12px;background:#fff7ed;border:1px solid #fed7aa;color:#9a3412;border-radius:8px;padding:11px 12px;display:flex;align-items:center;gap:8px;font-size:13px;font-weight:750}
.analytics-metrics{max-width:1360px;margin:0 auto 14px;display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px}
.analytics-metrics article{background:#fff;border:1px solid #e2e8f0;border-radius:8px;padding:14px;display:grid;gap:5px;min-width:0}
.analytics-metrics svg{color:#2563eb}
.analytics-metrics span{color:#667085;font-size:12px;font-weight:850}
.analytics-metrics strong{font-size:25px;line-height:1.1;color:#101828;font-variant-numeric:tabular-nums;overflow-wrap:anywhere}
.analytics-metrics small{color:#667085;font-size:11px;font-weight:700}
.analytics-panel{background:#fff;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;min-width:0}
.analytics-chart-panel,.analytics-visual-grid,.analytics-grid,.analytics-admin-shell>.analytics-panel{max-width:1360px;margin-left:auto;margin-right:auto}
.analytics-panel{width:100%}
.analytics-chart-panel{margin-bottom:14px}
.analytics-panel-head{min-height:50px;border-bottom:1px solid #eef2f7;padding:12px 14px;display:flex;align-items:center;justify-content:space-between;gap:10px}
.analytics-panel-head h2{margin:0;font-size:16px;color:#101828}
.analytics-panel-head p{margin:3px 0 0;color:#667085;font-size:12px;font-weight:650}
.analytics-panel-head span{color:#667085;font-size:12px;font-weight:800}
.analytics-visual-grid{margin-bottom:14px;display:grid;grid-template-columns:minmax(0,1.6fr) repeat(2,minmax(220px,1fr));gap:12px;align-items:stretch}
.analytics-visual-grid>.analytics-panel,.analytics-grid>.analytics-panel{margin:0;max-width:none}
.analytics-screen-chart .analytics-chart-content{padding:8px 8px 4px}
.analytics-donut-canvas{padding:6px 6px 0}
.analytics-chart-key{list-style:none;margin:0;padding:0 14px 10px;display:grid;gap:6px}
.analytics-chart-key li{display:flex;align-items:center;justify-content:space-between;gap:8px;color:#475467;font-size:12px;min-width:0}
.analytics-chart-key li>span{display:flex;align-items:center;gap:7px;min-width:0;overflow-wrap:anywhere}
.analytics-chart-key i{display:block;flex:0 0 8px;width:8px;height:8px;border-radius:50%}
.analytics-chart-key strong{color:#101828;font-variant-numeric:tabular-nums;white-space:nowrap}
.analytics-chart-key small{color:#667085;font-weight:750}
.analytics-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;margin-bottom:14px}
.analytics-list{display:grid}
.analytics-list-row{display:flex;align-items:center;justify-content:space-between;gap:12px;border-bottom:1px solid #eef2f7;padding:11px 14px}
.analytics-list-row:last-child{border-bottom:0}
.analytics-list-row>span{min-width:0;display:grid;gap:2px}
.analytics-list-row>span>strong{font-size:13px;color:#101828;overflow-wrap:anywhere}
.analytics-list-row>span>small{color:#667085;font-size:11px;overflow-wrap:anywhere}
.analytics-list-row em{font-style:normal;color:#2563eb;font-weight:900;font-variant-numeric:tabular-nums}
.analytics-user-identity{display:inline-flex;align-items:center;gap:8px;min-width:0;max-width:100%;font-weight:800;color:#101828}.analytics-user-identity>span:last-child{overflow-wrap:anywhere}.analytics-user-identity-large{font-size:16px}
.analytics-avatar{display:grid;place-items:center;flex:0 0 auto;overflow:hidden;border-radius:50%;background:#dbeafe;color:#1d4ed8;font-size:10px;font-weight:900;line-height:1}.analytics-avatar-small{width:24px;height:24px}.analytics-avatar-large{width:30px;height:30px;font-size:11px}.analytics-avatar-player{width:62px;height:62px;font-size:17px;border:3px solid #fff;box-shadow:0 5px 14px rgba(15,23,42,.18)}.analytics-avatar-hero{width:72px;height:72px;font-size:20px;border:3px solid rgba(255,255,255,.75);box-shadow:0 10px 24px rgba(2,6,23,.28)}.analytics-avatar img{width:100%;height:100%;object-fit:cover}
.analytics-list-button{width:100%;border:0;background:#fff;text-align:left;cursor:pointer;font:inherit}
.analytics-list-button:hover{background:#f8fbff}.analytics-list-button[aria-pressed=true]{background:#eff6ff;box-shadow:inset 3px 0 #2563eb}.analytics-list-button:focus-visible,.analytics-user-link:focus-visible,.analytics-icon-button:focus-visible{outline:3px solid #93c5fd;outline-offset:-3px}
.analytics-user-link{border:0;background:none;color:#1d4ed8;font:inherit;font-weight:800;padding:0;cursor:pointer;text-decoration:underline;text-underline-offset:2px}
.analytics-player-roster{overflow:visible}.analytics-player-grid{padding:14px;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}.analytics-player-card{min-width:0;border:1px solid #dbeafe;border-radius:14px;padding:14px 10px 12px;background:linear-gradient(145deg,#eff6ff,#fff);display:grid;justify-items:center;gap:7px;cursor:pointer;font:inherit;color:#101828;transition:transform .16s ease,box-shadow .16s ease,border-color .16s ease}.analytics-player-card:hover{transform:translateY(-2px);box-shadow:0 9px 20px rgba(37,99,235,.14);border-color:#60a5fa}.analytics-player-card[aria-pressed=true]{border-color:#2563eb;box-shadow:0 0 0 3px #dbeafe}.analytics-player-card:focus-visible{outline:3px solid #93c5fd;outline-offset:3px}.analytics-player-name{max-width:100%;font-size:13px;font-weight:900;overflow-wrap:anywhere;text-align:center}.analytics-player-count{color:#1d4ed8;font-size:16px;font-weight:900;font-variant-numeric:tabular-nums}.analytics-player-count small{color:#64748b;font-size:10px;font-weight:800}
.analytics-user-detail{margin-bottom:14px}.analytics-user-detail-head{align-items:flex-start;background:linear-gradient(135deg,#071f4f,#1463bd);border-bottom:0;padding:18px}.analytics-player-heading{display:grid;grid-template-columns:72px minmax(0,1fr);gap:12px;align-items:center;min-width:0}.analytics-player-heading>div{display:grid;gap:3px;min-width:0}.analytics-player-heading>div>span{color:#bfdbfe;text-transform:uppercase;font-size:10px;font-weight:900;letter-spacing:.08em}.analytics-player-heading h2{margin:0;color:#fff;font-size:22px;overflow-wrap:anywhere}.analytics-player-heading p{grid-column:2;margin:2px 0 0!important;color:#dbeafe!important;font-size:12px!important}.analytics-icon-button{flex:0 0 auto;display:grid;place-items:center;width:36px;height:36px;border:1px solid rgba(255,255,255,.35);border-radius:8px;background:rgba(255,255,255,.1);color:#fff;cursor:pointer}
.analytics-user-metrics{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));border-bottom:1px solid #eef2f7}.analytics-user-metrics>div{padding:14px;border-right:1px solid #eef2f7;display:grid;gap:5px}.analytics-user-metrics>div:last-child{border-right:0}.analytics-user-metrics span{font-size:11px;font-weight:850;color:#667085}.analytics-user-metrics strong{font-size:20px;line-height:1.1;color:#101828;font-variant-numeric:tabular-nums}
.analytics-user-insights{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:0;border-bottom:1px solid #eef2f7}.analytics-user-insights>div{padding:14px;border-right:1px solid #eef2f7;min-width:0}.analytics-user-insights>div:last-child{border-right:0}.analytics-user-insights h3{margin:0 0 10px;font-size:13px;color:#101828}
.analytics-compact-list{list-style:none;margin:0;padding:0;display:grid;gap:7px}.analytics-compact-list li{display:flex;justify-content:space-between;gap:8px;color:#475467;font-size:12px}.analytics-compact-list span{overflow-wrap:anywhere}.analytics-compact-list strong{color:#101828;font-variant-numeric:tabular-nums}.analytics-empty-copy{margin:0;color:#667085;font-size:12px;line-height:1.45}
.analytics-user-window{margin:0;display:grid;gap:9px}.analytics-user-window div{display:grid;gap:2px}.analytics-user-window dt{color:#667085;font-size:11px;font-weight:800}.analytics-user-window dd{margin:0;color:#344054;font-size:12px;font-weight:700;overflow-wrap:anywhere}
.analytics-state{min-height:160px;display:grid;place-items:center;text-align:center;color:#667085;font-size:14px;font-weight:750;padding:24px}
.analytics-state.compact{min-height:82px}
.analytics-state.denied{max-width:520px;margin:80px auto;background:#fff;border:1px solid #e2e8f0;border-radius:8px;gap:8px}
.analytics-state.denied h1{margin:0;color:#101828;font-size:24px}
.analytics-state.denied p{margin:0 0 8px;line-height:1.45}
.analytics-table-wrap{overflow:auto}
.analytics-table{width:100%;border-collapse:collapse;font-size:13px}
.analytics-table th{background:#f8fafc;color:#475467;font-size:11px;text-transform:uppercase;text-align:left;letter-spacing:.04em}
.analytics-table th,.analytics-table td{border-bottom:1px solid #eef2f7;padding:10px 12px;vertical-align:top;white-space:nowrap}
.analytics-table td{color:#344054}
.analytics-table strong{color:#101828}
.spin{animation:analytics-spin .9s linear infinite}
@keyframes analytics-spin{to{transform:rotate(360deg)}}
@media(max-width:980px){.analytics-admin-shell{padding:16px}.analytics-header{display:grid;align-items:start}.analytics-actions{justify-content:start}.analytics-metrics,.analytics-user-metrics{grid-template-columns:repeat(2,minmax(0,1fr))}.analytics-player-grid{grid-template-columns:repeat(4,minmax(0,1fr))}.analytics-user-metrics>div:nth-child(2){border-right:0}.analytics-user-metrics>div:nth-child(n+3){border-top:1px solid #eef2f7}.analytics-visual-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.analytics-screen-chart{grid-column:span 2}.analytics-grid{grid-template-columns:1fr}.analytics-user-insights{grid-template-columns:1fr}.analytics-user-insights>div{border-right:0;border-bottom:1px solid #eef2f7}.analytics-user-insights>div:last-child{border-bottom:0}}
@media(max-width:560px){.analytics-admin-shell{padding:12px}.analytics-header h1{font-size:24px}.analytics-actions{display:grid;grid-template-columns:1fr 1fr;width:100%}.analytics-actions button{grid-column:span 2}.analytics-metrics,.analytics-visual-grid,.analytics-user-metrics{grid-template-columns:1fr}.analytics-player-grid{grid-template-columns:repeat(2,minmax(0,1fr));padding:12px}.analytics-screen-chart{grid-column:auto}.analytics-user-metrics>div{border-right:0;border-top:1px solid #eef2f7}.analytics-user-metrics>div:first-child{border-top:0}.analytics-player-heading{grid-template-columns:58px minmax(0,1fr)}.analytics-avatar-hero{width:58px;height:58px}.analytics-table th,.analytics-table td{padding:9px 10px}}
.analytics-admin-shell{background:#f3f7fa;color:#143143;padding:32px 24px 64px}
.analytics-header{max-width:1400px;margin-bottom:24px;align-items:center}
.analytics-header h1{font-size:36px;font-weight:850;letter-spacing:-.035em;color:#102d40;margin:4px 0 7px}
.analytics-header p{font-size:14px;line-height:1.5;color:#567082}
.analytics-header .analytics-data-note{font-size:12px;margin-top:7px;color:#6b8392}
.analytics-eyebrow,.analytics-section-kicker{display:block;color:#147a9f;text-transform:uppercase;letter-spacing:.1em;font-size:11px;font-weight:900}
.analytics-back{color:#147a9f;margin-bottom:17px}
.analytics-actions{max-width:680px;gap:8px}
.analytics-source-filters{max-width:1400px;margin:0 auto 14px;display:flex;align-items:center;gap:7px;flex-wrap:wrap}
.analytics-source-filters>span{color:#557284;font-size:12px;font-weight:850;margin-right:4px}
.analytics-source-filters button{min-height:36px;border:1px solid #d6e4ea;border-radius:999px;padding:0 13px;background:#fff;color:#31576a;font:inherit;font-size:12px;font-weight:800;cursor:pointer}
.analytics-source-filters button[aria-pressed=true]{background:#147da5;border-color:#147da5;color:#fff}
.analytics-source-filters small{color:#6b8392;font-size:11px;font-weight:700}
.analytics-presets{width:100%;display:flex;justify-content:flex-end;gap:4px}
.analytics-presets button,.analytics-trend-switch button{border:1px solid #d6e4ea;background:#fff;color:#31576a;border-radius:8px;min-height:36px;padding:0 12px;font:inherit;font-size:12px;font-weight:800;cursor:pointer}
.analytics-presets button:hover,.analytics-trend-switch button:hover{background:#ebf5f7}
.analytics-actions label{font-size:11px;color:#4d6878}
.analytics-actions input{height:42px;border-color:#ccdde5}
.analytics-primary,.analytics-secondary{min-height:42px;border-radius:9px;font-size:12px}
.analytics-primary{background:#147da5}
.analytics-primary:hover:not(:disabled){background:#0f698e}
.analytics-panel,.analytics-metrics article{border-color:#e0eaf0;border-radius:15px;box-shadow:0 4px 22px rgba(26,65,87,.045)}
.analytics-metrics{max-width:1400px;grid-template-columns:repeat(4,minmax(0,1fr));gap:14px;margin-bottom:16px}
.analytics-metrics article{padding:20px;gap:9px;min-height:148px;align-content:start;position:relative;overflow:hidden}
.analytics-metric::before{content:'';position:absolute;top:0;left:0;right:0;height:4px;background:#9ab8ca}
.analytics-metric-growth::before{background:#1377a8}.analytics-metric-engagement::before{background:#16a79e}.analytics-metric-activity::before{background:#8b76d7}.analytics-metric-participation::before{background:#f4a340}
.analytics-metric-icon{display:grid;place-items:center;width:34px;height:34px;background:#e8f4f8;border-radius:10px;color:#147da5}
.analytics-metric-engagement .analytics-metric-icon{background:#e4f6f2;color:#11867f}.analytics-metric-activity .analytics-metric-icon{background:#f0ecfa;color:#7863bf}.analytics-metric-participation .analytics-metric-icon{background:#fff3e0;color:#b7731a}
.analytics-metrics span:not(.analytics-metric-icon){font-size:12px;color:#557284}
.analytics-metrics strong{font-size:34px;letter-spacing:-.04em;color:#102d40}
.analytics-metrics small{font-size:11px;line-height:1.45;color:#6c8593}
.analytics-growth-layout,.analytics-visual-grid,.analytics-grid,.analytics-admin-shell>.analytics-panel{max-width:1400px;margin-left:auto;margin-right:auto}
.analytics-detail-tabs{max-width:1400px;margin:0 auto 12px;display:flex;gap:6px;overflow-x:auto;padding:3px 2px 8px;scrollbar-width:thin}
.analytics-detail-tabs button{min-height:42px;flex:0 0 auto;border:1px solid #d9e6ec;border-radius:10px;background:#fff;color:#466476;padding:0 16px;font:inherit;font-size:13px;font-weight:850;cursor:pointer}
.analytics-detail-tabs button[aria-pressed=true]{background:#147da5;border-color:#147da5;color:#fff}
.analytics-detail-tabs button:hover:not([aria-pressed=true]){background:#eaf4f7}
.analytics-investigation-grid{grid-template-columns:1fr}
.analytics-signup-panel{margin-bottom:16px}
.analytics-signup-head>strong{font-size:13px;color:#147da5;white-space:nowrap}
.analytics-signup-table th,.analytics-signup-table td{padding:13px 20px}
.analytics-signup-table td:last-child{font-weight:700;color:#31576a}
.analytics-signup-pagination{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 20px;border-top:1px solid #edf2f5;color:#627d8d;font-size:12px;font-weight:750}
.analytics-signup-pagination>div{display:flex;gap:8px}
.analytics-signup-error{padding:20px;color:#9a3412}.analytics-signup-error p{margin:0 0 10px}
.analytics-growth-layout{display:grid;grid-template-columns:minmax(0,2fr) minmax(260px,1fr);gap:14px;margin-bottom:16px}
.analytics-chart-panel{margin:0}
.analytics-panel-head{padding:19px 20px;min-height:72px;border-color:#edf2f5;align-items:center}
.analytics-panel-head h2{font-size:17px;letter-spacing:-.015em;color:#17374a}
.analytics-panel-head p{font-size:12px;line-height:1.4;margin-top:5px;color:#708796}
.analytics-trend-head{align-items:flex-start;flex-wrap:wrap}
.analytics-trend-switch{display:flex;gap:4px}
.analytics-trend-switch button[aria-pressed=true]{background:#147da5;border-color:#147da5;color:white}
.analytics-growth-chart{padding:10px 15px 0}
.analytics-chart-data{border-top:1px solid #edf2f5;padding:12px 20px;color:#456679;font-size:12px}
.analytics-chart-data summary{cursor:pointer;font-weight:800;min-height:28px}
.analytics-insights{display:flex;flex-direction:column}
.analytics-insight-list{display:grid;grid-template-rows:repeat(3,1fr);flex:1}
.analytics-insight-list>div{display:grid;align-content:center;gap:5px;padding:15px 21px;border-bottom:1px solid #edf2f5}
.analytics-insight-list>div:last-child{border-bottom:0}
.analytics-insight-list span{font-size:12px;font-weight:800;color:#6a8492}
.analytics-insight-list strong{font-size:25px;line-height:1;color:#17374a;font-variant-numeric:tabular-nums}
.analytics-insight-list small{font-size:11px;color:#708796;line-height:1.4}
.analytics-engagement-grid{grid-template-columns:minmax(0,1.6fr) minmax(280px,1fr)}
.analytics-visual-grid{grid-template-columns:minmax(0,1.6fr) minmax(280px,1fr)}
.analytics-ranked-row{padding:10px 20px;border-bottom:1px solid #f0f4f6}
.analytics-ranked-row:last-child{border-bottom:0}
.analytics-ranked-heading{display:flex;justify-content:space-between;align-items:baseline;gap:12px;margin-bottom:8px}
.analytics-ranked-heading strong{font-size:12px;color:#254657}
.analytics-ranked-heading span{font-size:11px;color:#6c8593;text-align:right;white-space:nowrap}
.analytics-ranked-track,.analytics-player-track{display:block;height:6px;background:#eaf1f5;border-radius:999px;overflow:hidden}
.analytics-ranked-track span,.analytics-player-track i{display:block;height:100%;background:#16a79e;border-radius:999px}
.analytics-player-roster{overflow:hidden}
.analytics-player-grid{display:grid;grid-template-columns:1fr;gap:0;padding:0}
.analytics-player-card{display:grid;grid-template-columns:26px minmax(0,1fr) auto;gap:10px 11px;align-items:center;text-align:left;border:0;border-bottom:1px solid #edf2f5;border-radius:0;padding:11px 18px;background:white;box-shadow:none;transition:none;min-height:50px}
.analytics-player-card:hover{transform:none;box-shadow:none;background:#f4fafb}
.analytics-player-name{text-align:left;font-size:12px}
.analytics-player-count{font-size:12px;color:#33586a}.analytics-player-count small{font-size:10px}
.analytics-player-track{grid-column:2/4;height:4px}
.analytics-payment-panel .analytics-panel-head svg{color:#147da5}
.analytics-payment-value{padding:24px;display:grid;gap:10px}
.analytics-payment-value strong{font-size:30px;line-height:1.15;color:#17374a;font-variant-numeric:tabular-nums}
.analytics-payment-value small{color:#6c8593;font-size:12px}
.analytics-detail-anchor{max-width:1400px;margin:0 auto}.analytics-detail-anchor .analytics-user-detail{max-width:none}
.analytics-user-joined{margin:0;padding:10px 14px;color:#0e766e;background:#e9f8f5;font-size:12px;font-weight:800}
.analytics-failed{max-width:1400px;margin:16px auto;padding:26px}.analytics-failed h2{margin:0 0 7px}.analytics-failed p{color:#587284}
.analytics-table-wrap:focus-visible,.analytics-admin-shell button:focus-visible,.analytics-admin-shell a:focus-visible,.analytics-admin-shell input:focus-visible,.analytics-admin-shell summary:focus-visible{outline:3px solid #70c6d6;outline-offset:2px}
@media(max-width:980px){.analytics-growth-layout{grid-template-columns:1fr}.analytics-insight-list{grid-template-columns:repeat(3,1fr);grid-template-rows:1fr}.analytics-insight-list>div{border-bottom:0;border-right:1px solid #edf2f5}.analytics-insight-list>div:last-child{border-right:0}.analytics-metrics{grid-template-columns:repeat(2,minmax(0,1fr))}.analytics-engagement-grid,.analytics-visual-grid{grid-template-columns:1fr 1fr}.analytics-screen-chart{grid-column:auto}}
@media(max-width:650px){.analytics-admin-shell{padding:16px 12px 48px}.analytics-header h1{font-size:29px}.analytics-header{gap:20px}.analytics-actions{display:grid;grid-template-columns:1fr 1fr;width:100%}.analytics-presets{grid-column:1/-1;justify-content:flex-start}.analytics-actions>button{grid-column:auto}.analytics-actions input{width:100%}.analytics-metrics{gap:8px}.analytics-metrics article{padding:14px;min-height:137px}.analytics-metrics strong{font-size:29px}.analytics-metrics small{font-size:10px}.analytics-engagement-grid,.analytics-visual-grid{grid-template-columns:1fr}.analytics-insight-list{grid-template-columns:1fr}.analytics-insight-list>div{border-right:0;border-bottom:1px solid #edf2f5}.analytics-panel-head{padding:15px}.analytics-ranked-heading{display:grid;gap:2px}.analytics-ranked-heading span{text-align:left}.analytics-growth-chart{padding-left:4px;padding-right:8px}}
@media(max-width:650px){.analytics-signup-head{align-items:flex-start}.analytics-signup-table{min-width:600px}.analytics-signup-table th,.analytics-signup-table td{padding:12px}.analytics-signup-pagination{padding:12px;flex-wrap:wrap}.analytics-signup-pagination>div{width:100%}.analytics-signup-pagination button{flex:1}}
@media(max-width:360px){.analytics-metrics{grid-template-columns:1fr}.analytics-actions>button{grid-column:1/-1}}
@media(prefers-reduced-motion:reduce){.analytics-admin-shell *{scroll-behavior:auto!important;animation-duration:.01ms!important;transition-duration:.01ms!important}}
`;
